/**
 * Iter 222 — Canvas visuel des workflows (react-flow).
 *
 * Affiche le trigger en haut, puis les actions en cascade verticale.
 * - Click sur un nœud → ouvre l'inspecteur de propriétés (dans la sidebar)
 * - Drag des nœuds → repositionnement libre (positions sauvegardées)
 * - Ajout d'action via la palette à droite
 * - Suppression / réordonnancement via boutons sur le nœud
 *
 * Le format JSON est conservé identique : `{trigger, actions[]}`. Les positions
 * sont stockées dans `actions[i].ui.x` / `actions[i].ui.y` (et idem pour le trigger).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  applyNodeChanges,
  applyEdgeChanges,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Mail, MessageSquare, ListChecks, Tag, Pencil, FileText, Bell, Globe2,
  Sparkles, Cloud, GitBranch, Clock, Webhook, Plus, Trash2, Settings as SettingsIcon, Zap,
} from "lucide-react";

const TRIGGER_LABELS = {
  "lead.created": { label: "Nouveau lead", icon: "📥" },
  "lead.tag_added": { label: "Tag ajouté", icon: "🏷️" },
  "lead.score_threshold": { label: "Seuil score IA", icon: "🎯" },
  "lead.tier_changed": { label: "Tier changé", icon: "🔥" },
  "lead.converted": { label: "Lead converti", icon: "🎉" },
  "call.ended": { label: "Appel terminé", icon: "📞" },
  "call.transcribed": { label: "Appel transcrit", icon: "🎙️" },
  "form.submitted": { label: "Formulaire soumis", icon: "📝" },
  "stripe.payment_succeeded": { label: "Paiement Stripe OK", icon: "💰" },
  "stripe.payment_failed": { label: "Paiement Stripe échoué", icon: "💳" },
  "webhook.incoming": { label: "Webhook entrant", icon: "🪝" },
  "meta.message_received": { label: "Message Meta reçu", icon: "💬" },
};

const ACTION_META = {
  "email.send": { label: "Email", color: "#3b82f6", Icon: Mail },
  "sms.send": { label: "SMS", color: "#8b5cf6", Icon: MessageSquare },
  "task.create": { label: "Tâche", color: "#f59e0b", Icon: ListChecks },
  "tag.add": { label: "Ajouter tag", color: "#22c55e", Icon: Tag },
  "tag.remove": { label: "Retirer tag", color: "#94a3b8", Icon: Tag },
  "prospect.update": { label: "MAJ prospect", color: "#06b6d4", Icon: Pencil },
  "note.create": { label: "Note", color: "#64748b", Icon: FileText },
  "notif.internal": { label: "Notification", color: "#f43f5e", Icon: Bell },
  "webhook.outgoing": { label: "Webhook sortant", color: "#0ea5e9", Icon: Globe2 },
  "ai.gemini_generate": { label: "IA Gemini", color: "#a855f7", Icon: Sparkles },
  "onedrive.create_folder": { label: "Dossier OneDrive", color: "#0078d4", Icon: Cloud },
  "condition.if": { label: "Condition IF", color: "#eab308", Icon: GitBranch },
  "delay": { label: "Délai", color: "#94a3b8", Icon: Clock },
};

// ─── Custom node renderers ───────────────────────────────────────

function TriggerNode({ data, selected }) {
  const label = TRIGGER_LABELS[data.type] || { label: data.type, icon: "🪄" };
  return (
    <div
      className={`rounded-lg border-2 px-3 py-2 min-w-[180px] shadow-lg ${selected ? "border-violet-400" : "border-violet-500/60"}`}
      style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(99,102,241,0.05))" }}
      data-testid="canvas-trigger-node"
    >
      <div className="flex items-center gap-2">
        <span className="text-base">{label.icon}</span>
        <div className="min-w-0">
          <div className="text-[9px] uppercase font-bold text-violet-300">Trigger</div>
          <div className="text-xs text-white font-bold truncate">{label.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: "#a855f7" }} />
    </div>
  );
}

function ActionNode({ data, selected }) {
  const meta = ACTION_META[data.type] || { label: data.type, color: "#64748b", Icon: Webhook };
  const Icon = meta.Icon;
  return (
    <div
      className={`rounded-lg border-2 px-3 py-2 min-w-[200px] shadow-md ${selected ? "border-white" : "border-white/10"}`}
      style={{ backgroundColor: "#14101c" }}
      data-testid={`canvas-action-node-${data.index}`}
    >
      <Handle type="target" position={Position.Top} style={{ background: meta.color }} />
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${meta.color}26` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase font-bold" style={{ color: meta.color }}>
            #{data.index + 1} · {meta.label}
          </div>
          <div className="text-[11px] text-white font-medium truncate">{data.summary || "(non configuré)"}</div>
          {data.name && <div className="text-[9px] text-slate-500 truncate">name: {data.name}</div>}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: meta.color }} />
    </div>
  );
}

const NODE_TYPES = { trigger: TriggerNode, action: ActionNode };

// ─── Helpers ─────────────────────────────────────────────────────

function actionSummary(act) {
  if (!act?.config) return "—";
  const c = act.config;
  if (act.type === "email.send") return `${c.subject || "(sans sujet)"} → ${c.to || "?"}`;
  if (act.type === "sms.send") return `${(c.body || "").slice(0, 30)}…`;
  if (act.type === "task.create") return c.title || "Tâche";
  if (act.type === "tag.add" || act.type === "tag.remove") return (c.tags || []).join(", ") || "—";
  if (act.type === "delay") return `${c.minutes || 0} min`;
  if (act.type === "ai.gemini_generate") return (c.prompt || "").slice(0, 40) + "…";
  if (act.type === "condition.if") return `${c.field || "?"} ${c.op || "eq"} ${c.value ?? "?"}`;
  if (act.type === "onedrive.create_folder") return c.folder_name || "—";
  if (act.type === "note.create") return (c.text || "").slice(0, 40);
  if (act.type === "notif.internal") return c.title || "Notif";
  if (act.type === "webhook.outgoing") return c.url || "—";
  if (act.type === "prospect.update") return Object.keys(c.fields || {}).join(", ") || "—";
  return "—";
}

function buildGraph(wf) {
  if (!wf) return { nodes: [], edges: [] };
  const trigUi = wf.trigger?.ui || {};
  const nodes = [
    {
      id: "trigger",
      type: "trigger",
      position: { x: trigUi.x ?? 200, y: trigUi.y ?? 0 },
      data: { type: wf.trigger?.type || "lead.created" },
    },
  ];
  const edges = [];
  const actions = wf.actions || [];
  actions.forEach((a, i) => {
    const ui = a.ui || {};
    nodes.push({
      id: `act-${i}`,
      type: "action",
      position: { x: ui.x ?? 200, y: ui.y ?? 140 + i * 110 },
      data: {
        index: i,
        type: a.type,
        name: a.name,
        summary: actionSummary(a),
      },
    });
    edges.push({
      id: `e-${i}`,
      source: i === 0 ? "trigger" : `act-${i - 1}`,
      target: `act-${i}`,
      animated: true,
      style: { stroke: "#a855f7", strokeWidth: 2 },
    });
  });
  return { nodes, edges };
}

// ─── Main canvas component ───────────────────────────────────────

export default function WorkflowCanvas({ workflow, onChange, onAddAction, onRemoveAction, onSelectAction, selectedIndex }) {
  const initial = useMemo(() => buildGraph(workflow), [workflow]);
  const [nodes, setNodes] = useState(initial.nodes);
  const [edges, setEdges] = useState(initial.edges);
  const draggingRef = useRef(false);

  // Re-build graph when workflow changes (but not while user is dragging)
  useEffect(() => {
    if (draggingRef.current) return;
    const g = buildGraph(workflow);
    setNodes(g.nodes);
    setEdges(g.edges);
  }, [workflow]);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
    // If a position change finished, persist into workflow
    const posChanges = changes.filter((c) => c.type === "position" && !c.dragging);
    if (posChanges.length > 0 && workflow) {
      draggingRef.current = false;
      const updated = { ...workflow, actions: [...(workflow.actions || [])] };
      let dirty = false;
      for (const c of posChanges) {
        if (c.id === "trigger") {
          updated.trigger = { ...(updated.trigger || {}), ui: { ...(updated.trigger?.ui || {}), ...c.position } };
          dirty = true;
        } else if (c.id?.startsWith("act-")) {
          const idx = parseInt(c.id.slice(4), 10);
          if (Number.isInteger(idx) && updated.actions[idx]) {
            updated.actions[idx] = {
              ...updated.actions[idx],
              ui: { ...(updated.actions[idx].ui || {}), ...c.position },
            };
            dirty = true;
          }
        }
      }
      if (dirty && onChange) onChange(updated);
    } else if (changes.some((c) => c.type === "position" && c.dragging)) {
      draggingRef.current = true;
    }
  }, [workflow, onChange]);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  const handleNodeClick = useCallback((_e, node) => {
    if (node.id === "trigger") {
      onSelectAction?.(-1); // -1 = trigger
    } else if (node.id?.startsWith("act-")) {
      onSelectAction?.(parseInt(node.id.slice(4), 10));
    }
  }, [onSelectAction]);

  return (
    <div className="relative h-[640px] bg-[#0a0710] border border-white/5 rounded-xl overflow-hidden" data-testid="workflow-canvas">
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          fitView
          fitViewOptions={{ padding: 0.25 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#1a1425" gap={20} size={1} />
          <Controls className="!bg-[#14101c] !border-white/10" showInteractive={false} />
          <MiniMap
            nodeColor={(n) => {
              if (n.type === "trigger") return "#a855f7";
              const meta = ACTION_META[n.data?.type];
              return meta?.color || "#64748b";
            }}
            style={{ backgroundColor: "#0a0710" }}
            maskColor="rgba(20,16,28,0.8)"
            pannable
            zoomable
          />
        </ReactFlow>
      </ReactFlowProvider>

      {/* Floating "Add Action" palette */}
      <div className="absolute top-3 right-3 bg-[#14101c]/95 backdrop-blur border border-white/10 rounded-lg p-2 max-w-[200px] shadow-xl">
        <div className="text-[10px] uppercase font-bold text-slate-400 mb-1.5 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Ajouter une action
        </div>
        <div className="grid grid-cols-2 gap-1">
          {Object.entries(ACTION_META).map(([type, meta]) => {
            const Icon = meta.Icon;
            return (
              <button
                key={type}
                onClick={() => onAddAction?.(type)}
                className="flex flex-col items-center gap-0.5 p-1.5 rounded hover:bg-white/5 text-[9px] text-slate-300 border border-white/5"
                title={meta.label}
                data-testid={`canvas-add-${type}`}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                <span className="truncate max-w-full">{meta.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
